import React, { ReactElement } from 'react';
import { Helmet } from 'react-helmet';
import { ToastContainer } from 'react-toastify';
import { SearchAnalyticsProvider } from 'b2b-apps/core/analytics/context/search/SearchAnalyticsContext';
import { useStickyHeader } from 'b2b-apps/core/common/context/StickyHeaderProvider';
import { useActiveLanguage } from 'b2b-apps/core/i18n/hooks/useActiveLanguage';
import { useTranslate } from 'b2b-apps/core/i18n/hooks/useTranslate';
import { CookieBanner } from 'b2b-apps/shop/app/components/App/CookieBanner';
import { FeatureOnboarding } from 'b2b-apps/shop/app/components/App/FeatureOnboarding';
import { Footer } from 'b2b-apps/shop/app/components/App/Footer/Footer';
import { GlobalAlert } from 'b2b-apps/shop/app/components/App/GlobalAlert';
import { Header } from 'b2b-apps/shop/app/components/App/Header/Header';
import { ContentInfobars } from 'b2b-apps/shop/app/components/App/Infobars/ContentInfobars';
import { SessionInfobars } from 'b2b-apps/shop/app/components/App/Infobars/SessionInfobars';
import { ProductsComparisonBar } from 'b2b-apps/shop/app/components/App/ProductsComparisonBar';
import { Breadcrumbs } from 'b2b-apps/shop/breadcrumbs/components/Breadcrumbs';
import { BackToTop } from 'b2b-apps/shop/common/components/BackToTop';
import { Sticky } from 'b2b-apps/shop/common/components/Sticky';
import { Router } from 'b2b-apps/shop/routes/components/Router';
import { ComponentType, Props } from './';

const App: ComponentType = ({
  autosuggestRefCallback,
  isAutosuggestOpened,
  onCloseAutosuggest,
  onOpenAutosuggest,
}: Props): ReactElement => {
  const activeLanguage = useActiveLanguage();
  const { isEnabled, ref } = useStickyHeader();
  const t = useTranslate();

  return (
    <div className='App' onClick={onCloseAutosuggest}>
      <Helmet>
        <html lang={activeLanguage} />
        <title>{t('Shop Homepage\'s title')}</title>
        <meta name='description' content={t('Shop meta description', {}, 'Used as default page description')} />
        <link rel='apple-touch-icon' sizes='180x180' href='/assets/favicons/apple-touch-icon.png' />
        <link rel='icon' type='image/png' sizes='48x48' href='/assets/favicons/favicon-48x48.png' />
        <link rel='icon' type='image/png' sizes='32x32' href='/assets/favicons/favicon-32x32.png' />
        <link rel='icon' type='image/png' sizes='16x16' href='/assets/favicons/favicon-16x16.png' />
        <link rel='manifest' href='/assets/favicons/site.webmanifest' />
        <link rel='mask-icon' href='/assets/favicons/safari-pinned-tab.svg' color='#b70006' />
        <link rel='shortcut icon' href='/assets/favicons/favicon.ico' />
        <meta name='msapplication-config' content='/assets/favicons/browserconfig.xml' />
        <meta name='msapplication-TileColor' content='#b70006' />
        <meta name='theme-color' content='#ffffff' />
      </Helmet>

      <Sticky
        activeClassName='App-HeaderSticky'
        isEnabled={isEnabled}
        isFixedToWidowTop
      >
        <header className='App-Header' ref={ref}>
          <SearchAnalyticsProvider>
            <Header
              autosuggestRefCallback={autosuggestRefCallback}
              isAutosuggestOpened={isAutosuggestOpened}
              onCloseAutosuggest={onCloseAutosuggest}
              onOpenAutosuggest={onOpenAutosuggest}
            />
          </SearchAnalyticsProvider>
        </header>
      </Sticky>

      <main className='Main'>
        <FeatureOnboarding />

        <SessionInfobars />

        <ContentInfobars />

        <Breadcrumbs />

        <GlobalAlert />

        <div className='container'>
          <Router />
        </div>

      </main>

      <Footer />

      <BackToTop />

      <ToastContainer
        closeButton={false}
        newestOnTop={false}
        position='bottom-right'
      />

      <CookieBanner />

      <ProductsComparisonBar />
    </div>
  );
};

export { App };
